/**
 * Mutable document state behind `DocContainer.doc` and the TxDraft overlay (W1-03 core-model-ops).
 *
 * `DocState` is a `ChartDoc` whose tables can be written: the container's state owns plain `Map`s (copied from the
 * document it was created from, so that document is never mutated), and a draft's state layers `OverlayMap`s over
 * them: reads fall through to the container, writes stay in the overlay. Records themselves are immutable values
 * and are shared, never copied.
 */

import { withoutNegativeZero } from '../ops/equal';
import type {
  ChartDoc,
  EffectId,
  LineId,
  OrderListName,
  SingletonName,
  SingletonRecords,
  TableIds,
  TableName,
  TableRecords,
} from '../model/types';

/** A writable table (both `Map` and `OverlayMap` satisfy it). */
export interface MutTable<K, V> extends ReadonlyMap<K, V> {
  set(key: K, value: V): unknown;
  delete(key: K): boolean;
}

type Tables = { -readonly [T in TableName]: MutTable<TableIds[T], TableRecords[T]> };

export type DocState = {
  -readonly [K in keyof ChartDoc as K extends TableName ? never : K]: ChartDoc[K];
} & Tables;

/** Any record of any table (op payloads). */
export type AnyRecord = TableRecords[TableName];
export type AnyRecordId = TableIds[TableName];

export const TABLES: readonly TableName[] = [
  'bpm',
  'lines',
  'tracks',
  'keys',
  'notes',
  'effects',
  'videos',
  'markers',
  'groups',
  'generators',
  'assets',
];

export const SINGLETONS: readonly SingletonName[] = ['meta', 'pkg', 'timing', 'editor', 'rpeRoot'];

export const ORDER_LISTS: readonly OrderListName[] = ['lineOrder', 'effectOrder'];

/** Typed access to one table of a state. */
export function tableOf<T extends TableName>(s: DocState, tbl: T): MutTable<TableIds[T], TableRecords[T]> {
  return s[tbl] as MutTable<TableIds[T], TableRecords[T]>;
}

/** Untyped access (op application over the table union). */
export function anyTable(s: DocState, tbl: TableName): MutTable<AnyRecordId, AnyRecord> {
  return s[tbl];
}

/** Read-only table access on any document view. */
export function readTable(doc: ChartDoc, tbl: TableName): ReadonlyMap<AnyRecordId, AnyRecord> {
  return doc[tbl];
}

export function setSingleton<S extends SingletonName>(s: DocState, key: S, value: SingletonRecords[S]): void {
  (s as unknown as Record<SingletonName, SingletonRecords[SingletonName]>)[key] = value;
}

export function setOrderList(s: DocState, list: OrderListName, next: readonly number[]): void {
  if (list === 'lineOrder') s.lineOrder = next as readonly LineId[];
  else s.effectOrder = next as readonly EffectId[];
}

/** A state owning fresh Maps with the records of `doc` (the document itself is not touched). */
export function createBaseState(doc: ChartDoc): DocState {
  return {
    kind: doc.kind,
    version: doc.version,
    idSeq: doc.idSeq,
    meta: doc.meta,
    pkg: doc.pkg,
    timing: doc.timing,
    editor: doc.editor,
    rpeRoot: doc.rpeRoot,
    bpm: new Map(doc.bpm),
    lines: new Map(doc.lines),
    lineOrder: doc.lineOrder,
    tracks: new Map(doc.tracks),
    keys: new Map(doc.keys),
    notes: new Map(doc.notes),
    effects: new Map(doc.effects),
    effectOrder: doc.effectOrder,
    videos: new Map(doc.videos),
    markers: new Map(doc.markers),
    groups: new Map(doc.groups),
    generators: new Map(doc.generators),
    assets: new Map(doc.assets),
  };
}

/**
 * Maps every -0 in a state that owns its Maps (`createBaseState`) to 0, in place: changed records, singletons and
 * order lists are replaced by fixed copies, all others keep their identity. Used when a container is created, so a
 * document lifted from a file that wrote `-0.0` holds the same values as its snapshot (core/ops/equal.ts).
 */
export function canonicalizeNegativeZero(s: DocState): void {
  for (const key of SINGLETONS) {
    const cur = s[key];
    const fixed = withoutNegativeZero(cur);
    if (fixed !== cur) setSingleton(s, key, fixed);
  }
  for (const list of ORDER_LISTS) {
    const cur = s[list] as readonly number[];
    const fixed = withoutNegativeZero(cur);
    if (fixed !== cur) setOrderList(s, list, fixed);
  }
  for (const tbl of TABLES) {
    const table = anyTable(s, tbl);
    for (const [id, rec] of table) {
      const fixed = withoutNegativeZero(rec);
      if (fixed !== rec) table.set(id, fixed);
    }
  }
}

/** A state whose tables are overlays over `base` (writes never reach `base`). */
export function createOverlayState(base: DocState): DocState {
  return {
    kind: base.kind,
    version: base.version,
    idSeq: base.idSeq,
    meta: base.meta,
    pkg: base.pkg,
    timing: base.timing,
    editor: base.editor,
    rpeRoot: base.rpeRoot,
    bpm: new OverlayMap(base.bpm),
    lines: new OverlayMap(base.lines),
    lineOrder: base.lineOrder,
    tracks: new OverlayMap(base.tracks),
    keys: new OverlayMap(base.keys),
    notes: new OverlayMap(base.notes),
    effects: new OverlayMap(base.effects),
    effectOrder: base.effectOrder,
    videos: new OverlayMap(base.videos),
    markers: new OverlayMap(base.markers),
    groups: new OverlayMap(base.groups),
    generators: new OverlayMap(base.generators),
    assets: new OverlayMap(base.assets),
  };
}

/** An independent snapshot of any document view (fresh Maps; records shared). */
export function snapshotDoc(doc: ChartDoc): ChartDoc {
  return createBaseState(doc);
}

const GONE: unique symbol = Symbol('gone');
type Gone = typeof GONE;

/**
 * Map view layered over a base map: reads fall through, writes and deletions are kept in the overlay.
 * Iteration yields base entries first (with overlay replacements in place), then entries new in the overlay.
 */
export class OverlayMap<K, V> implements MutTable<K, V> {
  private readonly base: ReadonlyMap<K, V>;
  private readonly own = new Map<K, V | Gone>();
  private count: number;

  constructor(base: ReadonlyMap<K, V>) {
    this.base = base;
    this.count = base.size;
  }

  get size(): number {
    return this.count;
  }

  get [Symbol.toStringTag](): string {
    return 'OverlayMap';
  }

  get(key: K): V | undefined {
    if (this.own.has(key)) {
      const v = this.own.get(key);
      return v === GONE ? undefined : v;
    }
    return this.base.get(key);
  }

  has(key: K): boolean {
    if (this.own.has(key)) return this.own.get(key) !== GONE;
    return this.base.has(key);
  }

  set(key: K, value: V): this {
    if (!this.has(key)) this.count++;
    this.own.set(key, value);
    return this;
  }

  delete(key: K): boolean {
    if (!this.has(key)) return false;
    this.count--;
    if (this.base.has(key)) this.own.set(key, GONE);
    else this.own.delete(key);
    return true;
  }

  /** Number of keys written or deleted in the overlay. */
  get changedCount(): number {
    return this.own.size;
  }

  forEach(fn: (value: V, key: K, map: ReadonlyMap<K, V>) => void): void {
    for (const [k, v] of this.entries()) fn(v, k, this);
  }

  *entries(): MapIterator<[K, V]> {
    for (const [k, v] of this.base) {
      if (this.own.has(k)) {
        const o = this.own.get(k);
        if (o !== GONE && o !== undefined) yield [k, o];
      } else {
        yield [k, v];
      }
    }
    for (const [k, v] of this.own) {
      if (v !== GONE && !this.base.has(k)) yield [k, v];
    }
  }

  *keys(): MapIterator<K> {
    for (const [k] of this.entries()) yield k;
  }

  *values(): MapIterator<V> {
    for (const [, v] of this.entries()) yield v;
  }

  [Symbol.iterator](): MapIterator<[K, V]> {
    return this.entries();
  }
}
